import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Copy, Link2, RefreshCw, Search, Trash2, UserPlus } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import {
  useAcceptRequest,
  useCancelRequest,
  useFriendList,
  useIssueInvite,
  useMyInvite,
  useReceivedRequests,
  useRejectRequest,
  useRevokeInvite,
  useSentRequests,
} from '@/hooks/useFriends'
import { cn } from '@/lib/utils'
import type { Invite } from '@/api/friends'
import FriendRow from './FriendRow'

const inviteUrl = (token: string) => `${window.location.origin}/friends/invite/${token}`

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

const PAGE_SIZE = 10

type Props = {
  requestsOpen: boolean
  onRequestsOpenChange: (open: boolean) => void
}

export default function FriendListTab({ requestsOpen, onRequestsOpenChange }: Props) {
  const navigate = useNavigate()
  const [offset, setOffset] = useState(0)
  const [inviteOpen, setInviteOpen] = useState(false)
  const { data, isLoading } = useFriendList(offset, PAGE_SIZE)
  const { data: received } = useReceivedRequests()

  const receivedCount = received?.count ?? 0
  const total = data?.count ?? 0
  const items = data?.results ?? []
  const hasMore = offset + PAGE_SIZE < total

  return (
    <div className="flex flex-col">
      {/* ヘッダー：検索ボタン・申請受信バッジ */}
      <div className="flex items-center justify-end gap-2 py-2">
        <Button
          variant="outline"
          size="sm"
          className="relative"
          onClick={() => onRequestsOpenChange(true)}
        >
          <UserPlus size={16} />
          申請
          {receivedCount > 0 && (
            <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-destructive px-1 text-[11px] font-bold text-destructive-foreground">
              {receivedCount}
            </span>
          )}
        </Button>
        <Button variant="outline" size="sm" onClick={() => setInviteOpen(true)}>
          <Link2 size={16} />
          招待
        </Button>
        <Button variant="outline" size="sm" onClick={() => navigate('/friends/search')}>
          <Search size={16} />
          検索
        </Button>
      </div>

      {isLoading ? (
        <p className="py-10 text-center text-sm text-muted-foreground">読み込み中…</p>
      ) : items.length === 0 ? (
        <p className="py-10 text-center text-sm text-muted-foreground">
          まだフレンドがいません。検索から申請してみましょう。
        </p>
      ) : (
        <div className="divide-y">
          {items.map((f) => (
            <FriendRow
              key={f.id}
              nickname={f.nickname}
              handle={f.handle}
              avatarUrl={f.avatar_url}
              power={f.power}
              onClick={() => navigate(`/friends/${f.id}`)}
            />
          ))}
        </div>
      )}

      {hasMore && (
        <Button
          variant="ghost"
          className="mt-2"
          onClick={() => setOffset(offset + PAGE_SIZE)}
        >
          もっと見る
        </Button>
      )}

      <ReceivedRequestsSheet open={requestsOpen} onOpenChange={onRequestsOpenChange} />
      <InviteSheet open={inviteOpen} onOpenChange={setInviteOpen} />
    </div>
  )
}

function InviteSheet({
  open, onOpenChange,
}: { open: boolean; onOpenChange: (v: boolean) => void }) {
  // シートを開いている間だけ取得する。
  const { data: invite, isLoading } = useMyInvite(open)
  const issue = useIssueInvite()
  const revoke = useRevokeInvite()

  // 発行／再発行は成功と同時に自動コピーする。
  const handleIssue = (regenerate: boolean) => {
    issue.mutate(undefined, {
      onSuccess: async (created: Invite) => {
        const ok = await copyToClipboard(inviteUrl(created.token))
        toast.success(
          ok
            ? `招待リンクを${regenerate ? '再発行' : '発行'}してコピーしました（24時間有効）`
            : `招待リンクを${regenerate ? '再発行' : '発行'}しました（24時間有効）`,
        )
      },
    })
  }

  const handleCopy = async () => {
    if (!invite) return
    const ok = await copyToClipboard(inviteUrl(invite.token))
    toast[ok ? 'success' : 'error'](ok ? 'リンクをコピーしました' : 'コピーに失敗しました')
  }

  const handleRevoke = () => {
    revoke.mutate(undefined, {
      onSuccess: () => toast.success('招待リンクを無効化しました'),
    })
  }

  const busy = issue.isPending || revoke.isPending

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>招待リンク</SheetTitle>
        </SheetHeader>

        {isLoading ? (
          <p className="py-10 text-center text-sm text-muted-foreground">読み込み中…</p>
        ) : invite ? (
          <div className="space-y-4 py-2">
            <p className="text-xs text-muted-foreground">
              このリンクを共有すると、相手はあなたのプロフィールを見てフレンド申請できます。
            </p>
            <Input readOnly value={inviteUrl(invite.token)} onFocus={(e) => e.target.select()} />
            <p className="text-xs text-muted-foreground">
              有効期限：{new Date(invite.expires_at).toLocaleString()}
            </p>
            <div className="flex flex-col gap-2">
              <Button className="min-h-[44px]" onClick={handleCopy}>
                <Copy size={16} className="mr-1.5" />
                リンクをコピー
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 min-h-[44px]"
                  onClick={() => handleIssue(true)}
                  disabled={busy}
                >
                  <RefreshCw size={16} className="mr-1.5" />
                  再発行
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 min-h-[44px] text-destructive hover:text-destructive"
                  onClick={handleRevoke}
                  disabled={busy}
                >
                  <Trash2 size={16} className="mr-1.5" />
                  無効化
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <p className="text-xs text-muted-foreground">
              招待リンクを発行すると、フレンド以外の人にもプロフィールを共有できます。
            </p>
            <Button
              className="w-full min-h-[44px]"
              onClick={() => handleIssue(false)}
              disabled={busy}
            >
              <Link2 size={16} className="mr-1.5" />
              リンクを発行
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}

type RequestTab = 'received' | 'sent'

function ReceivedRequestsSheet({
  open, onOpenChange,
}: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const [tab, setTab] = useState<RequestTab>('received')

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>フレンド申請</SheetTitle>
        </SheetHeader>

        {/* 受信 / 送信 の切替 */}
        <div className="mt-2 flex border-b">
          {(['received', 'sent'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={cn(
                'flex-1 py-2 text-sm font-medium transition-colors',
                tab === t ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground',
              )}
            >
              {t === 'received' ? '受信' : '送信'}
            </button>
          ))}
        </div>

        {tab === 'received' ? <ReceivedList /> : <SentList />}
      </SheetContent>
    </Sheet>
  )
}

function ReceivedList() {
  const { data, isLoading } = useReceivedRequests()
  const accept = useAcceptRequest()
  const reject = useRejectRequest()
  const requests = data?.results ?? []

  if (isLoading) {
    return <p className="py-10 text-center text-sm text-muted-foreground">読み込み中…</p>
  }
  if (requests.length === 0) {
    return <p className="py-10 text-center text-sm text-muted-foreground">新しい申請はありません。</p>
  }

  return (
    <div className="divide-y">
      {requests.map((req) => (
        <FriendRow
          key={req.id}
          nickname={req.sender.nickname}
          handle={req.sender.handle}
          avatarUrl={req.sender.avatar_url}
          actions={
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={accept.isPending}
                onClick={() =>
                  accept.mutate(req.id, {
                    onSuccess: () => toast.success('フレンドになりました'),
                  })
                }
              >
                承認
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={reject.isPending}
                onClick={() => reject.mutate(req.id)}
              >
                拒否
              </Button>
            </div>
          }
        />
      ))}
    </div>
  )
}

function SentList() {
  const { data, isLoading } = useSentRequests()
  const cancel = useCancelRequest()
  const requests = data?.results ?? []

  if (isLoading) {
    return <p className="py-10 text-center text-sm text-muted-foreground">読み込み中…</p>
  }
  if (requests.length === 0) {
    return <p className="py-10 text-center text-sm text-muted-foreground">送信中の申請はありません。</p>
  }

  return (
    <div className="divide-y">
      {requests.map((req) => (
        <FriendRow
          key={req.id}
          nickname={req.receiver.nickname}
          handle={req.receiver.handle}
          avatarUrl={req.receiver.avatar_url}
          actions={
            <Button
              size="sm"
              variant="outline"
              disabled={cancel.isPending}
              onClick={() =>
                cancel.mutate(req.id, {
                  onSuccess: () => toast.success('申請を取り消しました'),
                })
              }
            >
              キャンセル
            </Button>
          }
        />
      ))}
    </div>
  )
}
